import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, History } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import type { PositionRow } from '../../types';
import { POSITION_STATUS } from '../../constants/tradingConstants';
import { getBrandClasses } from '../../config/tenantConfig';
import PnlFlash from '../common/PnlFlash';
import EmptyStateWithRedirect from '../common/EmptyStateWithRedirect';
import { pickRecentTradesEmpty } from '../../utils/emptyStates';
import { istParts } from '../../utils/dateUtils';

interface RecentTradesCardProps {
  positions: PositionRow[];
  loading?: boolean;
}

function formatTime(iso: string): string {
  try {
    const { hh, min } = istParts(iso);
    return `${hh}:${min}`;
  } catch {
    return iso;
  }
}

const exitTypeLabel: Record<string, string> = {
  SL: 'SL',
  EXIT: 'Target',
  EOD: 'EOD',
  MANUAL: 'Manual',
};

const RecentTradesCard: React.FC<RecentTradesCardProps> = ({ positions, loading }) => {
  const brand = getBrandClasses();
  const closed = positions
    .filter(p => p.status === POSITION_STATUS.CLOSED && p.exitTime)
    .sort((a, b) => new Date(b.exitTime!).getTime() - new Date(a.exitTime!).getTime())
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Recent Trades</CardTitle>
          {closed.length > 0 && (
            <Link to="/schedule?view=positions" className={`flex items-center gap-1 text-sm font-medium ${brand.link} ${brand.linkHover}`}>
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="px-6 py-8 text-center">
            <div className="animate-pulse space-y-3">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto" />
            </div>
          </div>
        ) : closed.length === 0 ? (
          <EmptyStateWithRedirect
            size="card"
            icon={<History className="w-8 h-8" />}
            {...pickRecentTradesEmpty()}
          />
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {closed.map(p => (
              <div key={p.positionId} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{p.symbol}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {p.side} &middot; {p.qty} qty &middot; {p.exitTime ? formatTime(p.exitTime) : ''}
                    {p.exitType && <span className="ml-1">&middot; {exitTypeLabel[p.exitType] ?? p.exitType}</span>}
                  </div>
                </div>
                <PnlFlash value={p.pnl} className="text-sm font-semibold flex-shrink-0 ml-3" />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentTradesCard;
